import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import CosmicChart from "../components/Landing/CosmicChart";
import AboutPage from "../components/About/AboutPage";
import { useState } from "react";
import "./Landing.css";
const Landing = () => {
  const { user } = useAuth();
  const [showAbout, setShowAbout] = useState(false);
  const [aboutSection, setAboutSection] = useState(null);

  const openAbout = (section) => {
    setAboutSection(section);
    setShowAbout(true);
  };

  return (
    <div className="landing">
      <section className="landing-hero">
        <div className="landing-hero__text">
          <p className="landing-pill">Heliocentric Astrology</p>
          <h1>Chart the Solar System from the Sun&apos;s point of view.</h1>
          <p>
            Heliocentric Astrology blends precise orbital mechanics with concise
            interpretations so you can understand the evolutionary mission encoded
            in every chart.
          </p>
          <div className="landing-hero__actions">
            <Link to="/app" className="btn primary">
              Explore Astrology
            </Link>
            {!user && (
              <Link to="/signup" className="btn ghost">
                Create Account
              </Link>
            )}
            <button
              className="app-about-button"
              onClick={() => setShowAbout(true)}
              aria-label="Learn about heliocentric astrology"
            >
              Learn More
            </button>
          </div>
        </div>
        <div className="landing-hero__image" aria-hidden>
          <CosmicChart />
        </div>
      </section>

      <section className="landing-grid">
        <article>
          <h3>Sun-Centered Astrology</h3>
          <p>
            Experience your chart through the Sun's timeless stillness. Watch the planets orbit your spiritual core, unveiling soul contracts, subtle energies and universal guidance.
          </p>
        </article>
        <article>
          <h3>Experience the Solar View</h3>
          <p>
            Shift your perspective to the heart of the solar system. Real-time heliocentric orbits show you astrology in motion.
          </p>
        </article>
        <article>
          <h3>The Soul's Solar View</h3>
          <p>
            Return to the center of your being. From the Sun's perspective, planetary motions reveal hidden soul intentions, karmic rhythms and your greater evolutionary path.
          </p>
        </article>
      </section>

      {/* Deep Dive Content Section */}
      <div className="bg-gray-50 py-24">
        <div className="max-w-5xl mx-auto px-6 space-y-24">

          {/* Introduction */}
          <section className="text-center space-y-8">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 tracking-tight">
              What is <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600">Heliocentric Astrology?</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Heliocentric astrology reveals your soul's divine blueprint by viewing the planets from the Sun's perspective-the heart of our solar system.
            </p>
            <div className="bg-white p-8 rounded-2xl shadow-xl border border-indigo-50 transform hover:scale-[1.02] transition-transform duration-500">
              <p className="text-lg text-gray-700 italic font-medium">
                "It's the difference between seeing who you are in this lifetime versus understanding why your soul chose to be here. Traditional astrology is your personal, Earth-based perspective. Heliocentric astrology is the <span className="text-indigo-600 not-italic font-bold">view from the CEO's office</span>-the solar system's objective, big-picture layout."
              </p>
            </div>

            {/* Precision Card */}
            <div className="bg-gradient-to-r from-slate-900 to-indigo-950 p-8 rounded-2xl shadow-xl text-left">
              <h3 className="text-xl md:text-2xl font-bold mb-3 text-white">
                Astronomically precise.{" "}
                <span className="text-purple-300">Spiritually meaningful.</span>
              </h3>
              <p className="text-indigo-200 leading-relaxed text-base">
                Heliocentric Astro is powered by Swiss Ephemeris and NASA JPL Horizons — the same calculation engines trusted by professional astrologers, astronomers, researchers and space scientists worldwide. Every planetary position, every dwarf planet, every aspect in your chart is calculated with the same precision used to track the actual movements of our solar system — the most trusted sources for accurate planetary positions, dwarf planet data and orbital mechanics, now in your hands.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                {["🔭 Swiss Ephemeris", "🛰️ NASA JPL Horizons"].map((badge, i) => (
                  <span
                    key={i}
                    className="px-4 py-1.5 bg-white/10 rounded-full text-sm font-semibold text-indigo-200 border border-white/20"
                  >
                    {badge}
                  </span>
                ))}
              </div>
            </div>
          </section>

          {/* Sun's Perspective */}
          <section className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block px-4 py-1.5 bg-yellow-100 text-yellow-800 rounded-full text-sm font-bold uppercase tracking-wide">
                The Sun's Perspective
              </div>
              <h3 className="text-3xl font-bold text-gray-900">Why it Matters</h3>
              <p className="text-gray-600 text-lg leading-relaxed">
                The Sun is the source of all life and light. When we view planetary positions from the center of consciousness itself, we access a higher octave of understanding.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                While the traditional geocentric chart is about your personality and daily emotions, the heliocentric chart illuminates your <span className="font-semibold text-purple-600">Soul's True Mission</span> and spiritual architecture.
              </p>
            </div>
            <div className="bg-gray-900 p-8 rounded-3xl text-white shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-purple-600/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
              <h4 className="text-xl font-bold mb-6 text-purple-300">It Reveals Hidden Patterns</h4>
              <ul className="space-y-4 relative z-2">
                {[
                  "Your Soul's True Mission",
                  "Divine Timing & Sacred Cycles",
                  "Pure Planetary Relationships",
                  "Your Spiritual Blueprint"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center text-xs">✨</span>
                    <span className="text-lg text-gray-200">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Benefits Grid */}
          <section>
            <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">The Benefits of Heliocentric Astrology</h3>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Spiritual Journey Card */}
              <div className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <h4 className="text-2xl font-bold text-indigo-900 mb-6 flex items-center gap-3">
                  <span className="text-3xl">🔮</span> For Your Spiritual Journey
                </h4>
                <ul className="space-y-4">
                  {[
                    { title: "Deeper Purpose", desc: "Move beyond 'what you're like' to 'why you're here'", section: "esoteric" },
                    { title: "Higher Guidance", desc: "Connect with your soul's wisdom and evolutionary direction", section: "higherOctave" },
                    { title: "Divine Plan", desc: "Recognize the sacred timing and cosmic choreography of your life", section: "introduction" },
                    { title: "Universal Flow", desc: "Sync with the actual movements of celestial bodies", section: "trueAngles" }
                  ].map((item, i) => (
                    <li key={i} className="group">
                      <button
                        onClick={() => openAbout(item.section)}
                        className="text-left w-full block text-gray-900 group-hover:text-indigo-600 transition-colors font-bold hover:underline decoration-indigo-600/30 underline-offset-4"
                      >
                        {item.title}
                      </button>
                      <span className="text-gray-600 block mt-1">{item.desc}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Personal Empowerment Card */}
              <div className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <h4 className="text-2xl font-bold text-purple-900 mb-6 flex items-center gap-3">
                  <span className="text-3xl">✨</span> For Personal Empowerment
                </h4>
                <ul className="space-y-4">
                  {[
                    { title: "Soul Meaning", desc: "Understand spiritual agreements and karmic lessons", section: "geocentricVsHeliocentric" },
                    { title: "Soul Gifts", desc: "Discover unique frequencies and vibrational signatures", section: "applications" },
                    { title: "Navigate Transitions", desc: "See pivotal moments as evolutionary gateways", section: "astronomyAstrology" },
                    { title: "Authentic Self", desc: "Live in alignment with your cosmic identity", section: "resonance" }
                  ].map((item, i) => (
                    <li key={i} className="group">
                      <button
                        onClick={() => openAbout(item.section)}
                        className="text-left w-full block text-gray-900 group-hover:text-purple-600 transition-colors font-bold hover:underline decoration-purple-600/30 underline-offset-4"
                      >
                        {item.title}
                      </button>
                      <span className="text-gray-600 block mt-1">{item.desc}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>



          {/* Comparison */}
          <section className="bg-indigo-900 rounded-3xl p-8 md:p-12 text-center text-white relative overflow-hidden">
            {/* Decorative circles */}
            <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <circle cx="0" cy="0" r="40" fill="white" />
                <circle cx="100" cy="100" r="40" fill="white" />
              </svg>
            </div>

            <div className="relative z-2 space-y-8">
              <div className="grid md:grid-cols-2 gap-8 items-center max-w-3xl mx-auto">
                <div className="text-indigo-200">
                  <p className="text-sm uppercase tracking-widest mb-2 font-semibold">Traditional Astrology Asks</p>
                  <p className="text-2xl font-serif italic">"How do the stars affect me?"</p>
                </div>
                <div className="hidden md:block h-12 w-px bg-indigo-700 mx-auto"></div>
                <div className="text-white">
                  <p className="text-sm uppercase tracking-widest mb-2 font-semibold text-purple-300">Heliocentric Astrology Reveals</p>
                  <p className="text-2xl font-serif italic">"What is my soul's relationship with the cosmic dance?"</p>
                </div>
              </div>
              <p className="text-lg text-indigo-100 font-medium pt-4 border-t border-indigo-700/50 mt-8 inline-block px-8">
                It's not about prediction, it's about <span className="text-white font-bold">revelation</span>. Not about fate, but <span className="text-white font-bold">divine design</span>.
              </p>
            </div>
          </section>

          {/* Audience & Science */}
          <section className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-7 space-y-6">
              <h3 className="text-3xl font-bold text-gray-900">A Higher Perspective for Conscious Seekers</h3>
              <p className="text-gray-600 text-lg">
                If you've ever felt that traditional astrology was helpful but somehow incomplete, heliocentric astrology is calling. It is the bridge between science and spirit, uniquely honoring both by using the true positions of the planets as observed from the center of our solar family.
              </p>
              <div className="space-y-3 pt-4">
                <p className="font-bold text-gray-900">Perfect for:</p>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {["Spiritual Seekers", "Conscious Individuals", "Truth Aligners", "Purpose Finders"].map((tag, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-700 bg-white border border-gray-200 px-3 py-2 rounded-lg text-sm">
                      <span className="text-indigo-500">✓</span> {tag}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="md:col-span-5 bg-gradient-to-br from-gray-900 to-indigo-900 p-8 rounded-3xl text-white flex flex-col justify-center">
              <h4 className="text-xl font-bold mb-4 flex items-center gap-2">
                🔭 Science & Spirit
              </h4>
              <p className="text-indigo-200 mb-6 leading-relaxed">
                You're not just reading about planets. You're connecting with the living cosmos as it truly is, from the heart of the Sun.
              </p>
              <div className="mt-auto">
                <p className="text-sm text-indigo-400 uppercase tracking-wider font-bold mb-2">Your Invitation</p>
                <p className="text-lg font-medium">
                  Step into a cosmic perspective that honors your soul's infinite nature.
                </p>
              </div>
            </div>
          </section>

          {/* How It Works Guide */}
          <section className="text-center space-y-12 pt-12 border-t border-gray-200">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Your Journey to <span className="text-indigo-600">Soul Clarity</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Four simple steps to download your personalized Heliocentric Soul Blueprint.
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8 relative">
              {[
                {
                  step: "01",
                  title: "Enter Birth Details",
                  desc: "Input your precise date, time, and location to calculate the exact solar planetary positions.",
                  icon: (
                    <svg className="w-8 h-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  )
                },
                {
                  step: "02",
                  title: "Choose Your Plan",
                  desc: "Select a Natal Report for soul purpose or a Transit Report for current timing.",
                  icon: (
                    <svg className="w-8 h-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  )
                },
                {
                  step: "03",
                  title: "Get Credits",
                  desc: "Purchase credits securely for the reports you need. No recurring subscriptions.",
                  icon: (
                    <svg className="w-8 h-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  )
                },
                {
                  step: "04",
                  title: "Receive PDF",
                  desc: "Your in-depth, personalized report is generated instantly for you to keep forever.",
                  icon: (
                    <svg className="w-8 h-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  )
                }
              ].map((item, i) => (
                <div key={i} className="relative group">
                  <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 h-full flex flex-col items-center">
                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      {item.icon}
                    </div>
                    <span className="absolute top-4 right-4 text-xs font-bold text-gray-300">
                      {item.step}
                    </span>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                  {/* Connector Line (Desktop Only) */}
                  {i < 3 && (
                    <div className="hidden md:block absolute top-14 -right-4 w-8 border-t-2 border-dashed border-gray-200 z-0" />
                  )}
                </div>
              ))}
            </div>

            <div className="pt-4">
              <Link
                to={user ? "/app" : "/login"}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-white bg-indigo-600 hover:bg-indigo-700 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 shadow-indigo-200"
              >
                <span>Start Your Journey</span>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <p className="mt-4 text-sm text-gray-500">
                Generate your chart instantly. No account required to explore.
              </p>
            </div>
          </section>
        </div>
      </div>

      <section className="py-24 bg-gray-900 border-t border-gray-800 relative overflow-hidden">
        {/* Background glow effect */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-purple-900/20 rounded-full blur-[100px] pointer-events-none" />

        <div className="relative z-2 max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 tracking-tight leading-tight">
            Ready to launch your <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-400">
              heliocentric workflow?
            </span>
          </h2>
          <p className="text-lg md:text-xl text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed">
            Start with the astrology app instantly. Sign up later to save settings
            and unlock personalized libraries.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {!user && (
              <Link
                to="/login"
                className="w-full sm:w-auto px-8 py-4 rounded-full font-medium text-white border border-gray-700 hover:bg-gray-800 hover:border-gray-600 transition-all duration-300 min-w-[160px]"
              >
                Login
              </Link>
            )}
            <Link
              to="/app"
              className="w-full sm:w-auto px-8 py-4 rounded-full font-bold text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:shadow-lg hover:checkbox-glow hover:scale-105 transition-all duration-300 min-w-[200px] shadow-purple-900/20"
            >
              Open Astrology App
            </Link>
          </div>
        </div>
      </section>
      {showAbout && (
        <AboutPage
          onClose={() => setShowAbout(false)}
          initialSection={aboutSection}
        />
      )}
    </div>
  );
};

export default Landing;


